import { Injectable } from '@angular/core';
import { Selector } from '@ngxs/store';
import { IQuoteInput, QuoteState } from '@nibtravel/shared/data-access/quote';
import {
  IQuotePriceSummary,
  IPlanSummary,
  IBasicOptionInput,
  IPlanOptionsSummary,
  ISpecifiedItemTypeSummary,
  ISpecifiedItemInput,
  IOptionItemPriceSummary,
  IOptionPriceSummary,
  ICancellationOptionInput,
} from '@nibtravel/shared/util/models';
import { PlansState } from '@nibtravel/shared/data-access/plans';

@Injectable()
export class PurchasePath {
  @Selector([QuoteState.planId, QuoteState.quotePrices])
  public static price(
    planId: number | undefined,
    prices: IQuotePriceSummary[] | undefined
  ): IQuotePriceSummary | undefined {
    return planId && prices
      ? prices.find((p) => p.planId === planId)
      : undefined;
  }

  @Selector([QuoteState.planId, QuoteState.quotePrices])
  public static planPosition(
    planId: number | undefined,
    prices: IQuotePriceSummary[] | undefined
  ): number {
    return planId && prices ? prices.findIndex((p) => p.planId === planId) : -1;
  }

  @Selector([QuoteState.planId, PlansState.getPlanSummaries])
  public static planSummary(
    planId: number | undefined,
    planSummaries: Record<number, IPlanSummary> | undefined
  ): IPlanSummary | undefined {
    return planId && planSummaries ? planSummaries[planId] : undefined;
  }

  @Selector([QuoteState.quoteInput])
  public static partnerCode(quoteInput: IQuoteInput): string | undefined {
    return quoteInput.partnerCode;
  }

  @Selector([QuoteState.quoteInput])
  public static campaignCode(quoteInput: IQuoteInput): string | undefined {
    return quoteInput.campaignCode;
  }

  @Selector([PurchasePath.planSummary])
  public static currencyCode(planSummary: IPlanSummary | undefined): string {
    return planSummary ? planSummary.currencyCode : 'AUD';
  }

  @Selector([QuoteState.quoteInput])
  public static basicOptions(
    quoteInput: IQuoteInput
  ): IBasicOptionInput[] | undefined {
    return quoteInput.basicOptions;
  }

  @Selector([PurchasePath.price])
  public static optionPrices(
    price: IQuotePriceSummary | undefined
  ): IOptionPriceSummary[] | undefined {
    return price ? price.basicOptionPrices : undefined;
  }

  @Selector([QuoteState.quoteInput])
  public static specifiedItemOptions(
    quoteInput: IQuoteInput
  ): ISpecifiedItemInput[] | undefined {
    return quoteInput.specifiedItemOption?.specifiedItems;
  }

  @Selector([QuoteState.planId, PlansState.planOptionsSummary])
  public static specifiedItemTypes(
    planId: number | undefined,
    planOptionSummary: Record<number, IPlanOptionsSummary> | undefined
  ): ISpecifiedItemTypeSummary[] | undefined {
    return planId && planOptionSummary
      ? planOptionSummary[planId].specifiedItemsOption?.specifiedItemTypes
      : undefined;
  }

  @Selector([PurchasePath.price])
  public static specifiedItemPrices(
    price: IQuotePriceSummary | undefined
  ): IOptionItemPriceSummary[] | undefined {
    return price && price.specifiedItemOptionPrice
      ? price.specifiedItemOptionPrice.items
      : undefined;
  }

  @Selector([QuoteState.quoteInput])
  static tripCancellation(
    quote: IQuoteInput
  ): ICancellationOptionInput | undefined {
    return quote.cancellationOption;
  }
}
